import { PageViewEvent } from '@shopify/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { mountReviews, disposeReviews } from '@theme/dl-reviews';

const PAGE_META = 'meta[name="description"],meta[name="robots"],meta[property^="og:"],meta[name^="twitter:"],link[rel="canonical"],link[rel="alternate"][hreflang]';
const PAGE_TYPES = new Set(['index', 'collection', 'list-collections', 'product', 'page', 'blog', 'article', 'search', 'policy', 'cart']);
const PREVIEW_PARAMS = ['preview_theme_id', 'preview_script_id', 'preview_key', '_fd'];

export function isStorePage(url, origin, root = '/') {
  if (url.origin !== origin || !/^https?:$/.test(url.protocol)) return false;
  if (['preview_theme_id', 'preview_script_id', 'preview_key', '_fd', 'section_id', 'sections', 'view'].some(key => url.searchParams.has(key))) return false;
  const prefix = root.replace(/\/$/, '');
  if (prefix && url.pathname !== prefix && !url.pathname.startsWith(`${prefix}/`)) return false;
  const path = url.pathname.slice(prefix.length) || '/';
  return /^(?:\/?|\/(?:collections|products|pages|blogs|policies)(?:\/[^.]*)?\/?|\/search\/?|\/cart\/?)$/.test(path);
}

export class DlNavigation {
  constructor() {
    this.main = document.querySelector('#MainContent[data-dl-navigation]');
    if (!this.main || window.Shopify?.designMode) return;
    this.root = this.main.dataset.routeRoot || '/';
    this.activeURL = new URL(location.href);
    this.previewContext = new URLSearchParams(this.activeURL.search);
    this.key = history.state?.dlNavigation?.key || this.newKey();
    this.positions = new Map();
    this.assets = new Map();
    this.sequence = 0;
    this.controller = new AbortController();
    const { signal } = this.controller;
    history.replaceState({ ...history.state, dlNavigation: { key: this.key } }, '', location.href);
    history.scrollRestoration = 'manual';
    document.addEventListener('click', event => this.onClick(event), { signal });
    document.addEventListener('submit', event => this.onSubmit(event), { signal });
    window.addEventListener('popstate', event => this.onPop(event), { signal });
    window.addEventListener('pagehide', () => this.request?.abort(), { signal });
  }

  newKey() { return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`; }
  allowed(url) {
    const candidate = new URL(url);
    for (const key of PREVIEW_PARAMS) {
      if (candidate.searchParams.has(key) && candidate.searchParams.get(key) === this.previewContext.get(key)) candidate.searchParams.delete(key);
    }
    return isStorePage(candidate, location.origin, this.root);
  }
  samePage(a, b) { return a.pathname === b.pathname && a.search === b.search; }
  rememberScroll() { this.positions.set(this.key, { x: scrollX, y: scrollY }); }

  onClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a[href]');
    if (!link || !link.closest('#MainContent,#shopify-section-dl-header,#shopify-section-dl-footer')) return;
    if (link.hasAttribute('download') || link.hasAttribute('hreflang') || link.hasAttribute('data-no-navigation') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href, location.href);
    if (!this.allowed(url) || (this.samePage(url, new URL(location.href)) && url.hash)) return;
    event.preventDefault();
    this.navigate(url);
  }

  onSubmit(event) {
    const form = event.target;
    if (event.defaultPrevented || !(form instanceof HTMLFormElement) || form.method.toLowerCase() !== 'get') return;
    if (!form.matches('.dl-collection-layout,.dl-search-form') || (form.target && form.target !== '_self')) return;
    const url = new URL(form.action, location.href);
    if (!this.allowed(url)) return;
    url.search = new URLSearchParams(new FormData(form, event.submitter)).toString();
    event.preventDefault();
    this.navigate(url);
  }

  onPop(event) {
    const url = new URL(location.href);
    if (!this.allowed(url)) { location.reload(); return; }
    this.rememberScroll();
    const key = event.state?.dlNavigation?.key || this.newKey();
    if (this.samePage(url, this.activeURL)) {
      this.request?.abort();
      this.sequence += 1;
      this.main.removeAttribute('aria-busy');
      delete document.documentElement.dataset.dlNavigating;
      this.key = key;
      this.activeURL = url;
      this.scrollToPage(url, this.positions.get(key));
      return;
    }
    this.navigate(url, { pop: true, key });
  }

  async navigate(url, { pop = false, key } = {}) {
    this.request?.abort();
    const request = this.request = new AbortController();
    const sequence = ++this.sequence;
    const timeout = setTimeout(() => request.abort(new Error('Navigation timed out')), 12000);
    this.rememberScroll();
    this.main.setAttribute('aria-busy', 'true');
    document.documentElement.dataset.dlNavigating = 'true';
    try {
      const response = await fetch(url.href, { signal: request.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
      const destination = new URL(response.url);
      destination.hash = url.hash;
      if (!response.ok || !this.allowed(destination) || !response.headers.get('content-type')?.includes('text/html')) throw new Error('Native page required');
      const html = await response.text();
      if (sequence !== this.sequence) return;
      if (request.signal.aborted) throw request.signal.reason;
      const page = new DOMParser().parseFromString(html, 'text/html');
      const incoming = page.querySelector('#MainContent[data-dl-navigation]');
      if (!page.body.classList.contains('dl-site') || !incoming || !PAGE_TYPES.has(incoming.dataset.template) || page.documentElement.lang !== document.documentElement.lang) throw new Error('Incompatible page');
      await this.prepareAssets(page, destination);
      if (sequence !== this.sequence) return;
      if (request.signal.aborted) throw request.signal.reason;
      clearTimeout(timeout);
      for (const menu of document.querySelectorAll('.dl-mobile-menu[open]')) menu.removeAttribute('open');
      if (!pop) {
        key = this.newKey();
        history.pushState({ dlNavigation: { key } }, '', destination.href);
      } else if (destination.href !== location.href || !history.state?.dlNavigation) {
        history.replaceState({ ...history.state, dlNavigation: { key } }, '', destination.href);
      }
      this.key = key;
      this.activeURL = destination;
      this.syncMeta(page);
      for (const name of [...document.body.classList]) if (name.startsWith('dl-template-')) document.body.classList.remove(name);
      for (const name of page.body.classList) if (name.startsWith('dl-template-')) document.body.classList.add(name);
      const nextMain = document.importNode(incoming, true);
      for (const script of nextMain.querySelectorAll('script[src]')) script.remove();
      for (const stylesheet of nextMain.querySelectorAll('link[rel="stylesheet"]')) stylesheet.remove();
      this.prepareReviewData(nextMain);
      disposeReviews(this.main);
      this.main.replaceWith(nextMain);
      this.main = nextMain;
      mountReviews(nextMain).then(ready => {
        if (ready || !nextMain.isConnected) return;
        const reviews = nextMain.querySelector('.dl-product-reviews');
        if (!reviews) return;
        const retry = document.createElement('a');
        retry.href = `${destination.pathname}${destination.search}#${reviews.id}`;
        retry.dataset.noNavigation = '';
        retry.className = 'dl-button';
        retry.textContent = 'Load customer reviews';
        reviews.append(retry);
      });
      if (typeof Theme !== 'undefined') Theme.template.name = nextMain.dataset.template;
      if (nextMain.dataset.productId) {
        try { RecentlyViewed.addProduct(nextMain.dataset.productId); } catch {}
      }
      document.dispatchEvent(new CustomEvent('dl:navigation', { detail: { url: destination.href, template: nextMain.dataset.template } }));
      document.dispatchEvent(new PageViewEvent({ page: { template: nextMain.dataset.template, title: document.title, url: destination.href } }));
      requestAnimationFrame(() => {
        if (sequence !== this.sequence) return;
        this.main.focus({ preventScroll: true });
        this.scrollToPage(destination, pop ? this.positions.get(key) : null);
      });
    } catch (error) {
      if (sequence !== this.sequence || (request.signal.aborted && request.signal.reason?.name === 'AbortError')) return;
      location.assign(url.href);
    } finally {
      clearTimeout(timeout);
      if (sequence === this.sequence) {
        this.main.removeAttribute('aria-busy');
        delete document.documentElement.dataset.dlNavigating;
      }
    }
  }

  async prepareAssets(page, destination) {
    const styles = [...page.querySelectorAll('link[rel="stylesheet"][href]')];
    const modules = [...page.querySelectorAll('script[type="module"][src]')].filter(script => /\/assets\/dl-(home|bead-hero|astrolabe-hero|customer-carousel|lattice-reading-form|catalog|product-film)\.js(?:\?|$)/.test(script.getAttribute('src')));
    const payment = [...page.querySelectorAll('script[src]')].filter(script => /\/cdn\/shopifycloud\/(portable-wallets\/|storefront\/assets\/shopify_pay\/)/.test(script.getAttribute('src')));
    await Promise.all([...styles, ...modules, ...payment].map(asset => this.loadAsset(asset, destination)));
  }

  loadAsset(source, destination) {
    const stylesheet = source.tagName === 'LINK';
    const attribute = stylesheet ? 'href' : 'src';
    const url = new URL(source.getAttribute(attribute), destination).href;
    if (this.assets.has(url)) return this.assets.get(url);
    const existing = [...document.querySelectorAll(stylesheet ? 'link[rel="stylesheet"][href]' : 'script[src]')].find(element => element[attribute] === url);
    // Section styles must outlive the outgoing main, including styles present on first load.
    if (stylesheet && existing?.closest('#MainContent')) document.head.append(existing);
    if (existing && (!stylesheet || existing.sheet)) return Promise.resolve();
    const promise = new Promise((resolve, reject) => {
      const element = existing || document.createElement(stylesheet ? 'link' : 'script');
      if (!existing) for (const { name, value } of source.attributes) element.setAttribute(name, value);
      const timeout = setTimeout(() => finish(new Error('Page asset timed out')), 8000);
      const onLoad = () => finish();
      const onError = () => finish(new Error('Page asset unavailable'));
      const finish = error => {
        clearTimeout(timeout);
        element.removeEventListener('load', onLoad);
        element.removeEventListener('error', onError);
        if (error) { this.assets.delete(url); if (!existing) element.remove(); reject(error); }
        else resolve();
      };
      element.addEventListener('load', onLoad, { once: true });
      element.addEventListener('error', onError, { once: true });
      if (!existing) {
        element.setAttribute(attribute, url);
        document.head.append(element);
      }
    });
    this.assets.set(url, promise);
    return promise;
  }

  prepareReviewData(main) {
    for (const original of main.querySelectorAll('script[data-dl-navigation-script="review-data"]')) {
      const script = document.createElement('script');
      script.textContent = original.textContent;
      document.head.append(script);
      script.remove();
      original.remove();
    }
  }

  syncMeta(page) {
    document.title = page.title;
    for (const old of document.head.querySelectorAll(PAGE_META)) old.remove();
    for (const meta of page.head.querySelectorAll(PAGE_META)) document.head.append(document.importNode(meta, true));
  }

  scrollToPage(url, position) {
    if (position) { window.scrollTo({ left: position.x, top: position.y, behavior: 'instant' }); return; }
    let target;
    try { target = url.hash && document.getElementById(decodeURIComponent(url.hash.slice(1))); } catch {}
    if (target) target.scrollIntoView({ behavior: 'instant' });
    else window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }
}

new DlNavigation();
